import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import HeaderDashboard from "../../Components/HeaderDashboard";
import $ from "jquery";
import "jquery-ui-dist/jquery-ui";
import "jquery-ui-dist/jquery-ui.css";
import { AuthContext } from "../../Routes/AuthProvider";
import { getHotelCancellationReport } from "../../Services/HotelSearchApi";
import * as XLSX from "xlsx";

const CancelledBookings = () => {
  const { userName } = useContext(AuthContext);
  const [globalSearch, setGlobalSearch] = useState("");
  const [filters, setFilters] = useState({
    bookingRefNo: "",
    fromDate: "",
    toDate: "",
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 20;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const convertToDate = (str) => {
    if (!str) return null;
    const [day, month, year] = str.split("/");
    return new Date(`${year}-${month}-${day}`);
  };

  useEffect(() => {
    $("#fromDate").datepicker({
      dateFormat: "dd/mm/yy",
      maxDate: 0,
      onSelect: function (dateText) {
        setFilters((prev) => ({
          ...prev,
          fromDate: dateText,
        }));
      },
    });

    $("#toDate").datepicker({
      dateFormat: "dd/mm/yy",
      maxDate: 0,
      onSelect: function (dateText) {
        setFilters((prev) => ({
          ...prev,
          toDate: dateText,
        }));
      },
    });
  }, []);

  useEffect(() => {
    if (filters.fromDate) {
      const minD = convertToDate(filters.fromDate);
      $("#toDate").datepicker("option", "minDate", minD);
    }
  }, [filters.fromDate]);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const data = await getHotelCancellationReport(userName);

      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data?.cancellationReport && Array.isArray(data.cancellationReport)) {
        list = data.cancellationReport;
      } else if (data?.data && Array.isArray(data.data)) {
        list = data.data;
      } else if (data?.data?.cancellationReport && Array.isArray(data.data.cancellationReport)) {
        list = data.data.cancellationReport;
      } else if (data?.bookings && Array.isArray(data.bookings)) {
        list = data.bookings;
      }

      //console.log("Extracted Cancellation List:", list);

      if (filters.bookingRefNo) {
        list = list.filter((item) => {
          const ref = (
            item.bookingRefId ||
            item.BookingRefId ||
            item.confirmationNo ||
            item.ConfirmationNo ||
            item.referenceNo ||
            item.ReferenceNo ||
            ""
          ).toLowerCase();
          return ref.includes(filters.bookingRefNo.toLowerCase());
        });
      }

      if (filters.fromDate || filters.toDate) {
        const fromLimit = filters.fromDate ? convertToDate(filters.fromDate) : null;
        if (fromLimit) fromLimit.setHours(0, 0, 0, 0);

        const toLimit = filters.toDate ? convertToDate(filters.toDate) : null;
        if (toLimit) toLimit.setHours(23, 59, 59, 999);

        list = list.filter((item) => {
          const itemDateStr = item.createdAt || item.CreatedAt || item.bookingDate || item.BookingDate || item.cancellationDate || item.CancellationDate;
          if (!itemDateStr) return false;
          const itemDate = new Date(itemDateStr);
          if (isNaN(itemDate.getTime())) return false;

          if (fromLimit && itemDate < fromLimit) return false;
          if (toLimit && itemDate > toLimit) return false;
          return true;
        });
      }

      setTransactions(list);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching hotel cancellation reports:", error);
      alert("Failed to fetch cancellation reports. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (userName) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName]);

  // Local search filter
  const filteredTransactions = transactions.filter((item) => {
    if (!globalSearch) return true;
    const term = globalSearch.toLowerCase();
    const ref = (
      item.bookingRefId ||
      item.BookingRefId ||
      item.confirmationNo ||
      item.ConfirmationNo ||
      item.referenceNo ||
      item.ReferenceNo ||
      ""
    ).toLowerCase();
    const status = (item.bookingStatus || item.BookingStatus || "CANCELLED").toLowerCase();
    const conf = (item.confirmationNo || item.ConfirmationNo || "").toLowerCase();
    const pnr = (item.pnr || item.PNR || item.hotelPNR || item.HotelPNR || "").toLowerCase();
    const hotel = (item.hotelName || item.HotelName || "").toLowerCase();
    const city = (item.cityName || item.CityName || "").toLowerCase();
    return (
      ref.includes(term) ||
      status.includes(term) ||
      conf.includes(term) ||
      pnr.includes(term) ||
      hotel.includes(term) ||
      city.includes(term)
    );
  });

  const handleExportExcel = () => {
    const dataToExport = filteredTransactions.map((item, index) => ({
      "Sr No": index + 1,
      "Ref No": item.bookingRefId || item.BookingRefId || item.referenceNo || item.ReferenceNo || "",
      "Booking Status": item.bookingStatus || item.BookingStatus || "CANCELLED",
      "Confirmation No": item.confirmationNo || item.ConfirmationNo || "",
      "Hotel PNR": item.hotelPNR || item.HotelPNR || item.pnr || item.PNR || "",
      "Booking Date": item.createdAt || item.CreatedAt || item.bookingDate || item.BookingDate
        ? new Date(item.createdAt || item.CreatedAt || item.bookingDate || item.BookingDate).toLocaleDateString("en-GB")
        : "",
      "Cancelled Date": item.cancellationDate || item.CancellationDate
        ? new Date(item.cancellationDate || item.CancellationDate).toLocaleDateString("en-GB")
        : "",
      "Amount": item.totalPrice || item.TotalPrice || item.amount || item.Amount || 0,
      "Hotel Name": item.hotelName || item.HotelName || "",
      "City": item.cityName || item.CityName || item.sector || item.Sector || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CancelledBookings");
    XLSX.writeFile(workbook, "Cancelled_Bookings_Report.xlsx");
  };

  // Calculate pagination values
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredTransactions.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredTransactions.length / recordsPerPage);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        end = 4;
      } else if (currentPage >= totalPages - 1) {
        start = totalPages - 3;
      }

      if (start > 2) {
        pageNumbers.push("...");
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      if (end < totalPages - 1) {
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  return (
    <>
      <HeaderDashboard />

      <div className="toolbar py-2" id="kt_toolbar">
        <div className="container-fluid d-flex align-items-center">
          <div className="flex-grow-1">
            <div className="page-title d-flex align-items-center flex-wrap">
              <h1 className="text-gray-900 fw-bold fs-3">Reports</h1>
              <span className="h-20px border-gray-200 border-start mx-3"></span>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-1">
                <li className="breadcrumb-item text-muted">Hotel</li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-200 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-gray-900">
                  Cancelled Bookings
                </li>
              </ul>
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <div className="flex-shrink-0 me-2 d-none-mob">
              <ul className="nav" role="tablist">
                <li className="nav-item" role="presentation">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.history.back();
                    }}
                  >
                    <img src="/assets/images/back.png" width={30} alt="back" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ui-datepicker {
          z-index: 9999 !important;
        }
      `}</style>
      <section className="wraper">
        <div className="box_shadow mt-14">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label fw-bold">From Date</label>
              <input
                type="text"
                id="fromDate"
                className="form-control"
                placeholder="From Date"
                value={filters.fromDate}
                style={{ cursor: "pointer" }}
                readOnly
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-bold">To Date</label>
              <input
                type="text"
                id="toDate"
                className="form-control"
                placeholder="To Date"
                value={filters.toDate}
                style={{ cursor: "pointer" }}
                readOnly
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Reference No</label>
              <input
                className="form-control"
                type="text"
                name="bookingRefNo"
                value={filters.bookingRefNo}
                onChange={handleChange}
                placeholder="Ref No"
              />
            </div>
            
            <div className="col-md-2">
              <label className="form-label fw-bold" style={{ visibility: "hidden" }}>Search</label>
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? "..." : "Search"}
              </button>
            </div>
          </div>
        </div>

        <div className="box_shadow mt-4">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: "15px" }}>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-success px-4"
                style={{ backgroundColor: "#17c752", borderColor: "#17c752", fontWeight: "600" }}
                onClick={handleExportExcel}
              >
                <i className="fa fa-file-excel me-2"></i> Excel
              </button>
            </div>
            <div className="position-relative" style={{ width: "300px" }}>
              <input
                type="text"
                className="form-control"
                style={{ paddingLeft: "35px" }}
                placeholder="Search Ref No, Hotel, Confirmation No..."
                value={globalSearch}
                onChange={(e) => {
                  setGlobalSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <span className="position-absolute top-50 translate-middle-y ms-3" style={{ left: 0 }}>
                <i className="fa fa-search text-muted"></i>
              </span>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table align-middle gs-0 gy-4 table-hover">
              <thead>
                <tr className="fw-bold bg-light">
                  <th className="ps-4">Sr No</th>
                  <th>RefNo.</th>
                  <th>Booking Status</th>
                  <th>ConfirmationNo</th>
                  <th>Hotel PNR</th>
                  <th>Booking Date</th>
                  <th>Cancelled Date</th>
                  <th>Amount</th>
                  <th>Hotel Name</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.length > 0 ? (
                  currentRecords.map((item, index) => (
                    <tr key={index}>
                      <td className="ps-4">{indexOfFirstRecord + index + 1}</td>
                      <td>
                        <span className="text-gray-800 fw-bold">
                          {item.bookingRefId ||
                            item.BookingRefId ||
                            item.confirmationNo ||
                            item.ConfirmationNo ||
                            item.referenceNo ||
                            item.ReferenceNo}
                        </span>
                      </td>
                      <td>
                        <span className="text-danger fw-semibold">
                          {item.bookingStatus || item.BookingStatus || "CANCELLED"}
                        </span>
                      </td>
                      <td>
                        <span className="text-gray-600">
                          {item.confirmationNo ||
                            item.ConfirmationNo ||
                            item.pnr ||
                            item.PNR ||
                            "-"}
                        </span>
                      </td>
                      <td>
                        <span className="text-gray-600">
                          {item.hotelPNR ||
                            item.HotelPNR ||
                            "-"}
                        </span>
                      </td>
                      <td>
                        <span className="text-gray-600">
                          {item.createdAt ||
                          item.CreatedAt ||
                          item.bookingDate ||
                          item.BookingDate
                            ? new Date(
                                item.createdAt ||
                                  item.CreatedAt ||
                                  item.bookingDate ||
                                  item.BookingDate,
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "-"}
                        </span>
                      </td>
                      <td>
                        <span className="text-gray-600">
                          {item.cancellationDate || item.CancellationDate
                            ? new Date(item.cancellationDate || item.CancellationDate).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "-"}
                        </span>
                      </td>
                      <td>
                        <span className="fw-bold text-gray-900">
                          ₹{" "}
                          {(
                            item.totalPrice ||
                            item.TotalPrice ||
                            item.amount ||
                            item.Amount
                          )?.toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <span className="text-gray-800">
                          {item.hotelName || item.HotelName} -{" "}
                          {item.cityName ||
                            item.CityName ||
                            item.sector ||
                            item.Sector}
                        </span>
                      </td>
                      <td className="text-center">
                        <Link
                          to={`/reservations/hotel/TransactionsDetails?referenceno=${item.bookingRefId || item.BookingRefId || item.referenceNo}`}
                          className="btn btn-sm btn-dark px-4 py-1"
                          style={{ background: "#110942" }}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center py-5">
                      {loading ? (
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      ) : (
                        <span className="text-muted">
                          No transactions found
                        </span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {transactions.length > 0 && (
            <div className="d-flex justify-content-between align-items-center flex-wrap mt-4 border-top pt-4">
              <div className="text-muted fs-7 mb-2">
                Showing <span className="fw-bold text-gray-800">{indexOfFirstRecord + 1}</span> to{" "}
                <span className="fw-bold text-gray-800">
                  {Math.min(indexOfLastRecord, transactions.length)}
                </span> of{" "}
                <span className="fw-bold text-gray-800">{transactions.length}</span> entries
              </div>
              <nav aria-label="Table pagination">
                <ul className="pagination pagination-circle pagination-outline justify-content-end mb-0" style={{ gap: "4px" }}>
                  <li className={`page-item previous ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      style={{ cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                    >
                      <i className="fa fa-chevron-left fs-8"></i>
                    </button>
                  </li>
                  {getPageNumbers().map((page, index) => {
                    if (page === "...") {
                      return (
                        <li key={`ellipsis-${index}`} className="page-item disabled">
                          <span className="page-link border-0">...</span>
                        </li>
                      );
                    }
                    return (
                      <li
                        key={page}
                        className={`page-item ${currentPage === page ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                          style={{
                            backgroundColor: currentPage === page ? "#110942" : "transparent",
                            borderColor: currentPage === page ? "#110942" : "",
                            color: currentPage === page ? "#fff" : "",
                            fontWeight: currentPage === page ? "600" : "400"
                          }}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item next ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      style={{ cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                    >
                      <i className="fa fa-chevron-right fs-8"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CancelledBookings;
